import {useEffect, useState} from "react";
import {Game} from "@game/game";
import {Tile} from "@game/map/tile";
import {Vector2} from "@utility/vector2";
import {TILE_SIZE, COLOURS} from "@components/core/theme";
import {CANDIDATE_OFFSETS, H, W, dotInSvg, labelLayout} from "@components/map/PlayerNameLabel";

interface IPlayerLabels {
    game: Game
    nearbyTiles: Tile[]
    excludeTiles?: Set<string>
}

function tileScore(nearbyTiles: Tile[], pos: Vector2): number {
    const tile = nearbyTiles.find(t => t.position.x === pos.x && t.position.y === pos.y);
    if (!tile) return -1;
    const hasObstruction = !!tile.place && ["block", "road", "decoration"].includes(tile.place.type);
    return hasObstruction ? 0 : tile.letter !== undefined ? 1 : 2;
}

function bestOffset(nearbyTiles: Tile[], pos: Vector2): Vector2 {
    let best = CANDIDATE_OFFSETS[0];
    let bestScore = -2;
    for (const offset of CANDIDATE_OFFSETS) {
        const score = tileScore(nearbyTiles, pos.plus(offset));
        if (score > bestScore) { bestScore = score; best = offset; }
        if (bestScore === 2) break;
    }
    return best;
}

// Among the tiles of the player's most recent word, find the first tile (reading order)
// that has a usable adjacent position, and return that tile + the best offset direction.
function findLabelAnchor(playerTiles: Tile[], nearbyTiles: Tile[]): { anchorTile: Vector2, offset: Vector2 } | null {
    // Group by word (placed ID), pick the most recent word
    const words = new Map<number, Tile[]>();
    for (const tile of playerTiles) {
        const placed = tile.letter!.placed!;
        if (!words.has(placed)) words.set(placed, []);
        words.get(placed)!.push(tile);
    }
    let maxPlaced = -Infinity;
    let wordTiles = playerTiles;
    for (const [placed, tiles] of Array.from(words.entries())) {
        if (placed > maxPlaced) { maxPlaced = placed; wordTiles = tiles; }
    }

    // Sort tiles in reading order (left-to-right, top-to-bottom = first letter of word first)
    const sorted = [...wordTiles].sort((a, b) =>
        a.position.x !== b.position.x ? a.position.x - b.position.x : a.position.y - b.position.y
    );

    // Try each tile; prefer the first one whose best adjacent tile is within the loaded area
    let fallback: { anchorTile: Vector2, offset: Vector2 } | null = null;
    for (const tile of sorted) {
        const offset = bestOffset(nearbyTiles, tile.position);
        if (tileScore(nearbyTiles, tile.position.plus(offset)) >= 0) {
            return { anchorTile: tile.position, offset };
        }
        if (!fallback) fallback = { anchorTile: tile.position, offset };
    }
    return fallback;
}

export function PlayerLabels({game, nearbyTiles, excludeTiles}: IPlayerLabels) {
    const [, setTick] = useState(0);

    useEffect(() => {
        const update = () => setTick(t => t + 1);
        game.events.delegates.add(update);
        return () => game.events.delegates.remove(update);
    }, [game]);

    // Group all placed-letter tiles by player ID
    const groups = new Map<string, Tile[]>();
    for (const tile of nearbyTiles) {
        const player = tile.letter?.player;
        if (!player || tile.letter?.placed === undefined) continue;
        if (!groups.has(player)) groups.set(player, []);
        groups.get(player)!.push(tile);
    }

    const labels: JSX.Element[] = [];
    for (const [playerId, tiles] of Array.from(groups.entries())) {
        const nickname = game.getNickname(playerId);
        if (!nickname) continue;

        const anchor = findLabelAnchor(tiles, nearbyTiles);
        if (!anchor) continue;
        if (excludeTiles?.has(`${anchor.anchorTile.x},${anchor.anchorTile.y}`)) continue;

        const { anchorTile, offset } = anchor;
        const dx = offset.x;
        const dy = offset.y;
        const px = anchorTile.x * TILE_SIZE.x;
        const py = anchorTile.y * TILE_SIZE.y;

        const { svgW, pathD, left, top } = labelLayout(dx, dy, px, py);
        const pathId = `name-arc-${playerId}`;
        const dot = dotInSvg(dx, dy, svgW, left, top, px, py);
        // dy > 0 arc starts exactly at the dot — offset text slightly along the path
        const textOffset = dx === 0 && dy > 0 ? 12 : 0;

        labels.push(
            <svg key={playerId} width={svgW} height={H} overflow="visible"
                 style={{ position: "absolute", left, top, pointerEvents: "none", userSelect: "none" }}>
                <defs>
                    <path id={pathId} d={pathD}/>
                    <filter id={`name-shadow-${playerId}`} x="-10%" y="-30%" width="120%" height="160%">
                        <feDropShadow dx="0" dy="0.5" stdDeviation="0.8"
                            floodColor={COLOURS.parchment} floodOpacity="0.9"/>
                    </filter>
                </defs>
                {/* Parchment halo */}
                <text fontFamily="Georgia, 'Times New Roman', serif" fontSize="11.5"
                      letterSpacing="0.8" fill={COLOURS.parchment}
                      strokeWidth="3" stroke={COLOURS.parchment} strokeLinejoin="round" opacity="0.7">
                    <textPath href={`#${pathId}`} startOffset={textOffset || undefined}>{nickname}</textPath>
                </text>
                {/* Main label */}
                <text fontFamily="Georgia, 'Times New Roman', serif" fontSize="11.5"
                      letterSpacing="0.8" fill={COLOURS.amber} filter={`url(#name-shadow-${playerId})`}>
                    <textPath href={`#${pathId}`} startOffset={textOffset || undefined}>{nickname}</textPath>
                </text>
                {/* Town dot */}
                <circle cx={dot.cx} cy={dot.cy} r={6}
                        fill={COLOURS.amber} stroke={COLOURS.parchment} strokeWidth={1}/>
            </svg>
        );
    }

    return <>{labels}</>;
}
